<template>
    <div>
        <div
            v-if="deadline.OriginalDueDate"
            class="my-2">
            {{ generateDeadlineChangeInfo }}
        </div>
        <div
            v-if="showDueDateControls"
            class="row">
            <div v-bind:class="responsiveClasses.newDueDateSelect">
                <pc-date
                    id="newDeadlineDueDateInput"
                    v-model="newDueDate"
                    name="newDeadlineDueDateInput"
                    v-bind:validator="newDueDateValidator"
                    v-bind:label="selectANewDueDatePrompt"
                    v-on:validated="onDueDateValidated" />
            </div>
            <pc-button-row v-bind:class="responsiveClasses.dueDateButtons">
                <template v-slot:right>
                    <pc-button v-on:click="updateDueDateCancel">
                        {{ cancelText }}
                    </pc-button>
                    <pc-button
                        v-bind:disabled="hasValidationError || !newDueDate"
                        primary
                        v-on:click="updateDueDateSave">
                        {{ saveText }}
                    </pc-button>
                </template>
            </pc-button-row>
        </div>
        <div
            v-else-if="showEndDateControls"
            class="row">
            <div v-bind:class="responsiveClasses.newDueDateSelect">
                <pc-date
                    id="newEndDueDateInput"
                    v-model="newEndDate"
                    name="newDeadlineEndDateInput"
                    v-bind:label="selectANewEndDatePrompt" />
            </div>
            <pc-button-row v-bind:class="responsiveClasses.dueDateButtons">
                <template v-slot:right>
                    <pc-button v-on:click="updateEndDateCancel">
                        {{ cancelText }}
                    </pc-button>
                    <pc-button
                        v-bind:disabled="!newEndDate"
                        primary
                        v-on:click="updateEndDateSave">
                        {{ saveText }}
                    </pc-button>
                </template>
            </pc-button-row>
        </div>
        <pc-button-row v-else>
            <pc-button
                v-if="deadline.DueDate && $app.LoggedInRole.RolePrivileges.ProjectAdminChangeDeadlineDate"
                v-on:click="updateDueDate">
                {{ changeDueDateButton }}
            </pc-button>
            <pc-button
                v-if="$app.LoggedInRole.RolePrivileges.ProjectAdminSatisfyDeadline"
                id="MarkSatisfiedBtn"
                v-on:click="updateSatisfied">
                {{ updateStatusButtonText }}
            </pc-button>
            <pc-button
                v-if="showPauseButton"
                id="PauseBtn"
                v-on:click="pauseDeadline">
                {{ pauseDeadlineButtonText }}
            </pc-button>
            <pc-button
                v-if="showResumeButton"
                id="ResumeBtn"
                v-on:click="resumeDeadline">
                {{ resumeDeadlineButtonText }}
            </pc-button>
            <pc-button
                v-if="userIsCpAdmin && deadline.SatisfiedDate"
                id="ChangeEndDateBtn"
                warning
                v-on:click="updateEndDate">
                Change Satisfied Date
            </pc-button>
        </pc-button-row>
    </div>
</template>

<script>
    import { DEADLINE_ACTIVITY_STATUS_ID } from "Util/clientDeadline";
    import { getValidator, VALIDATOR_TYPES } from "Validation/validator";
    import { ClientDate, dateStrToClientDate } from "Util/clientDate";
    import { WIDE_BP } from "Projects/SharedCapsules/_sharedCapsuleBase";
    import { KEYS, NAMESPACE } from "./i18n/deadlineActionRow.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { mapI18n } from "Util/i18n";
    import { isAtLeast, getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        newDueDateSelect: {
            mb: { xs: 2, [WIDE_BP.name]: 0 },
            col: { xs: 12, [WIDE_BP.name]: 6 }
        },
        dueDateButtons: {
            col: { xs: 12, [WIDE_BP.name]: "" },
            "align-content": { xs: "center", [WIDE_BP.name]: "end" }
        }
    };

    export default {
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            deadline: { type: Object, required: true }
        },
        emits: ["update-due-date", "update-end-date", "update-satisfied", "pause-deadline", "resume-deadline"],
        data() {
            return {
                showDueDateControls: false,
                showEndDateControls: false,

                // Use existing DueDate as initial value for new DueDate
                // earlyStr adds one day to prevent allowing the new DueDate to be the current date
                newDueDate: this.deadline && this.deadline.DueDate && this.deadline.DueDate.Display,
                newDueDateValidator: getValidator(this.$app, [
                    {
                        type: VALIDATOR_TYPES.dateRange,
                        earlyStr: new ClientDate(this.$app).addDays(1).toString()
                    },
                    VALIDATOR_TYPES.date,
                    VALIDATOR_TYPES.required
                ]),
                newDueDateValidationResult: null,
                newEndDate: this.deadline && this.deadline.SatisfiedDate && this.deadline.SatisfiedDate.Display
            };
        },
        computed: {
            ...mapI18n({ ...KEYS }, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            initialDueDateDisplay() {
                return this.deadline && this.deadline.DueDate && this.deadline.DueDate.Display;
            },
            generateDeadlineChangeInfo() {
                return this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.dueDateChangedMessage,
                    values: {
                        OriginalDueDate: this.deadline.OriginalDueDate.Display,
                        LastChangedUserName: this.deadline.LastChangedUserName
                    }
                });
            },
            updateStatusButtonText() {
                if (this.deadline.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.finished) {
                    return this.markUnsatisfiedText;
                }
                return this.markSatisfiedText;
            },
            hasValidationError() {
                return this.newDueDateValidationResult && this.newDueDateValidationResult.hasError;
            },
            wideView() {
                return isAtLeast(this.viewport, WIDE_BP);
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            userIsCpAdmin() {
                return this.$app.loggedInUserIsCPAdmin;
            },
            showPauseButton() {
                return (
                    this.deadline.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.running &&
                    (this.$app.loggedInUserIsCPAdmin ||
                        this.$app.LoggedInRole.RolePrivileges.ProjectAdminPauseResumeDeadline)
                );
            },
            showResumeButton() {
                return (
                    this.deadline.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.paused &&
                    (this.$app.loggedInUserIsCPAdmin ||
                        this.$app.LoggedInRole.RolePrivileges.ProjectAdminPauseResumeDeadline)
                );
            }
        },
        methods: {
            updateDueDate() {
                this.showDueDateControls = true;
            },
            updateDueDateSave() {
                if (this.hasValidationError) {
                    return;
                }

                if (!this.newDueDate) {
                    return;
                }

                // Don't allow changing due date to the current due date; this actually would
                // update the time of day of the due date which is very non-obvious
                if (this._areDatesEqual(this.initialDueDateDisplay, this.newDueDate)) {
                    this.showDueDateControls = false;
                    return;
                }

                this.$emit("update-due-date", this.deadline, this.newDueDate);
                this.showDueDateControls = false;
            },
            updateDueDateCancel() {
                this.newDueDate = this.initialDueDateDisplay;
                this.showDueDateControls = false;
            },
            updateEndDate() {
                this.showEndDateControls = true;
            },
            updateEndDateSave() {
                if (this.hasValidationError || !this.newEndDate) {
                    return;
                }
                this.$emit("update-end-date", this.deadline, this.newEndDate);
                this.showEndDateControls = false;
            },
            updateEndDateCancel() {
                this.showEndDateControls = false;
            },
            updateSatisfied() {
                this.$emit("update-satisfied", this.deadline);
            },
            onDueDateValidated(result) {
                this.newDueDateValidationResult = result;
            },
            // Takes 2 valid, non-null date strings and checks whether they correspond
            // to the same date.
            _areDatesEqual(dateStr1, dateStr2) {
                const clientDate1 = dateStrToClientDate(this.$app, dateStr1);
                const clientDate2 = dateStrToClientDate(this.$app, dateStr2);
                return clientDate1.equals(clientDate2);
            },
            resumeDeadline() {
                this.$emit("resume-deadline", this.deadline, true);
            },
            pauseDeadline() {
                this.$emit("pause-deadline", this.deadline, true);
            }
        }
    };
</script>
